/**
 * ARCH-F01 (Phase 24 3-A) — React error boundary.
 *
 * Before this commit there was zero ErrorBoundary /
 * componentDidCatch / getDerivedStateFromError usage anywhere in
 * the codebase. Any unhandled JS exception (malformed LLM
 * response, null paragraph ref, Konva event on an unmounted
 * component, etc.) crashed the full React tree to a blank white
 * screen with no recovery path — the drafter would see an empty
 * page and not even a generic error message.
 *
 * This boundary catches errors thrown during render / lifecycle /
 * constructor of children and renders a recovery fallback with a
 * "Try again" button that clears the error state. The label prop
 * is logged alongside the error so the operator can tell from the
 * server-side replay stream which workbench (Spec / Drawing /
 * Matter) crashed.
 *
 * Out of scope (deferred):
 *   - Error boundaries don't catch async errors (Promise rejection
 *     without try/catch). Per-call try/catch in the Zustand stores
 *     is the right surface for those — already in place for the
 *     spec generate path and getting the same treatment in
 *     Phase 25 (3-B verifySection).
 *   - Server-side reporting (Sentry / posthog beacon on
 *     componentDidCatch). Console-only today; can be layered in
 *     a follow-up without touching the boundary's render.
 */
import React from 'react';

interface State { hasError: boolean; message: string; }

interface Props {
  children: React.ReactNode;
  label?: string;
}

export class ErrorBoundary extends React.Component<Props, State> {
  state: State = { hasError: false, message: '' };

  static getDerivedStateFromError(err: Error): State {
    return { hasError: true, message: err.message };
  }

  componentDidCatch(err: Error, info: React.ErrorInfo): void {
    // eslint-disable-next-line no-console -- intentional diagnostic
    console.error(
      `[ErrorBoundary:${this.props.label || 'unknown'}]`,
      err, info,
    );
  }

  private handleReset = (): void => {
    this.setState({ hasError: false, message: '' });
  };

  render(): React.ReactNode {
    if (!this.state.hasError) {
      return this.props.children;
    }
    return (
      <div
        role="alert"
        aria-live="assertive"
        style={{
          padding: '32px',
          maxWidth: '640px',
          margin: '64px auto',
          textAlign: 'center',
          border: '1px solid #e4e4e7',
          borderRadius: '6px',
          backgroundColor: '#fff',
          boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
        }}
        data-testid={
          this.props.label
            ? `error-boundary-${this.props.label.toLowerCase()}`
            : 'error-boundary'
        }
      >
        <h2 style={{ marginTop: 0, color: '#18181b', fontSize: '18px' }}>
          Something went wrong
        </h2>
        <p style={{ color: '#52525b', fontSize: '14px', lineHeight: 1.5 }}>
          {this.props.label
            ? `The ${this.props.label} view encountered an unexpected error.`
            : 'An unexpected error occurred.'}
          {' '}
          Try again, or reload the page if the problem persists.
        </p>
        {this.state.message && (
          <p
            style={{
              color: '#71717a',
              fontSize: '12px',
              fontFamily: 'ui-monospace, monospace',
              marginTop: '12px',
              padding: '8px 12px',
              backgroundColor: '#f4f4f5',
              borderRadius: '4px',
              wordBreak: 'break-word',
            }}
          >
            {this.state.message}
          </p>
        )}
        <button
          type="button"
          onClick={this.handleReset}
          style={{
            marginTop: '20px',
            padding: '8px 20px',
            backgroundColor: '#002FA7',
            color: '#fff',
            border: 'none',
            borderRadius: '4px',
            fontSize: '14px',
            fontWeight: 500,
            cursor: 'pointer',
          }}
          data-testid="error-boundary-reset"
        >
          Try again
        </button>
      </div>
    );
  }
}

export default ErrorBoundary;
